import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {useCallback, useMemo, useState} from 'react'

import {createQuickFilterAnchor, type QuickFilterProps} from './quick-filter-shared'
import {
  getRepoPullsFilterPickerTokens,
  getRepoPullsPositiveFilterPickerTokens,
  replaceRepoPullsFilterInQuery,
} from './quick-filter-query'

const labelAnchor = createQuickFilterAnchor('Label')
const projectAnchor = createQuickFilterAnchor('Projects')
const milestoneAnchor = createQuickFilterAnchor('Milestones')

export function LabelQuickFilter({owner, query, repo, onQueryChange, structuralUpdatesEnabled}: QuickFilterProps) {
  const labelNames = useMemo(
    () => getRepoPullsPositiveFilterPickerTokens(query, 'label', structuralUpdatesEnabled),
    [query, structuralUpdatesEnabled],
  )
  const onSelectionChange = useCallback(
    (labels: LabelPickerLabel[]) => {
      onQueryChange(
        replaceRepoPullsFilterInQuery(
          query,
          labels.map(label => label.name),
          'label',
          structuralUpdatesEnabled,
        ),
        'label',
      )
    },
    [onQueryChange, query, structuralUpdatesEnabled],
  )

  return (
    <LabelPicker
      anchorElement={labelAnchor}
      title="Filter by labels"
      showEditLabelsButton={false}
      repo={repo}
      owner={owner}
      readonly={false}
      onSelectionChange={onSelectionChange}
      labels={[]}
      labelNames={labelNames.length > 0 ? labelNames : undefined}
      canCreateLabel={false}
      enableSuggestedLabels
    />
  )
}

export function ProjectQuickFilter({owner, query, repo, onQueryChange, structuralUpdatesEnabled}: QuickFilterProps) {
  const projectTokens = useMemo(
    () => getRepoPullsFilterPickerTokens(query, 'project', structuralUpdatesEnabled),
    [query, structuralUpdatesEnabled],
  )
  const onSelectionChange = useCallback(
    (projects: ProjectPickerBaseProject[]) => {
      const projectValues = projects.map(project => `${owner}/${project.number}`)
      const nextQuery = replaceRepoPullsFilterInQuery(query, projectValues, 'project', structuralUpdatesEnabled)
      if (nextQuery !== query) onQueryChange(nextQuery, 'projects')
    },
    [onQueryChange, owner, query, structuralUpdatesEnabled],
  )
  const getSelectedProjects = useCallback(
    (projects: ProjectPickerBaseProject[]) =>
      projects.filter(project =>
        projectTokens.some(token => project.url.endsWith(`/${token.replace('/', '/projects/')}`)),
      ),
    [projectTokens],
  )

  return (
    <ProjectPicker
      pickerId="repo-pulls-project-filter"
      anchorElement={projectAnchor}
      repo={repo}
      owner={owner}
      onSave={onSelectionChange}
      selectedProjects={[]}
      readonly={false}
      getSelectedProjects={getSelectedProjects}
      title="Filter by projects"
    />
  )
}

export function MilestoneQuickFilter({owner, query, repo, onQueryChange, structuralUpdatesEnabled}: QuickFilterProps) {
  const milestoneTokens = useMemo(
    () => getRepoPullsFilterPickerTokens(query, 'milestone', structuralUpdatesEnabled),
    [query, structuralUpdatesEnabled],
  )
  const [activeMilestone, setActiveMilestone] = useState<MilestonePickerMilestone | null>(null)
  const currentMilestone = activeMilestone && milestoneTokens.includes(activeMilestone.title) ? activeMilestone : null
  const onSelectionChange = useCallback(
    (milestones: MilestonePickerMilestone[]) => {
      setActiveMilestone(milestones[0] ?? null)
      onQueryChange(
        replaceRepoPullsFilterInQuery(
          query,
          milestones.map(milestone => milestone.title),
          'milestone',
          structuralUpdatesEnabled,
        ),
        'milestone',
      )
    },
    [onQueryChange, query, structuralUpdatesEnabled],
  )

  return (
    <MilestonePicker
      repo={repo}
      owner={owner}
      activeMilestone={currentMilestone}
      anchorElement={milestoneAnchor}
      onSelectionChange={onSelectionChange}
      title="Filter by milestones"
      canCreateMilestone={false}
    />
  )
}
